import { MinusCircleOutlined, ExclamationCircleFilled, InboxOutlined } from '@ant-design/icons'
import { PageContainer } from '@ant-design/pro-layout'
import { makeStyles } from '@material-ui/styles'
import { Button, Col, Divider, Form, Input, Modal, Row, Typography as Text, message } from 'antd'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReportCard from '@/components/ReportCard'
import dimorderApi from '@/libs/api/dimorderApi'
import { useSelector } from '@/redux'

import colors from '@/style/colors'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    textAlign: 'center',
  },
  text: {
    fontSize: 16,
  },
  boldText: {
    fontSize: 15,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  btn: {
    backgroundColor: colors.button,
    borderColor: colors.button,
    borderRadius: 5,
    color: colors.white,
    width: '100%',
    fontSize: 15,
    height: 35,
  },
})

export default function RabbitPayAccount(props) {
  const { t } = useTranslation()
  const classes = useStyles(props)
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const [isAddingKey, setIsAddingKey] = useState(false)
  const [apiKeyStatus, setApiKeyStatus] = useState({ isSet: false, maskedKey: '' })

  const getRabbitPayKey = async () => {
    try {
      const response = await dimorderApi.instance(activeMerchant.id)?.getRabbitPayApiKey()
      const statusData = response?.data || { isSet: false, maskedKey: '' }
      setApiKeyStatus(statusData)
    } catch (error) {
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.submitError'),
      })
    }
  }

  useEffect(() => {
    if (activeMerchant) {
      getRabbitPayKey()
    }
  }, [activeMerchant])

  const onFinish = async (values) => {
    try {
      await dimorderApi.instance(activeMerchant.id)?.setRabbitPayApiKey(values.apiKey)
      setIsAddingKey(false)
      message.success(t('app.common.success'))
      getRabbitPayKey()
    } catch (error) {
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.submitError'),
      })
    }
  }

  const handleDeleteApiKey = () => {
    Modal.confirm({
      title: t('page.rabbitPayAccount.alert.deleteKey'),
      icon: <ExclamationCircleFilled />,
      content: t('page.rabbitPayAccount.alert.deleteKeyDescription'),
      okText: t('app.common.yes'),
      okType: 'danger',
      cancelText: t('app.common.no'),
      async onOk() {
        try {
          await dimorderApi.instance(activeMerchant.id)?.deleteRabbitPayApiKey()
          setIsAddingKey(false)
          message.success(t('app.common.success'))
          getRabbitPayKey()
        } catch (error) {
          Modal.error({
            title: t('app.common.error'),
            content: t('app.common.submitError'),
          })
        }
      },
    })
  }

  function CurrentApiKey() {
    if (!apiKeyStatus.isSet) {
      return (
        <Row style={{ justifyContent: 'center', padding: '25px' }}>
          <Col
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <InboxOutlined style={{ fontSize: '55px' }} />
            <Text className={classes.boldText}>{t('page.rabbitPayAccount.noKey')}</Text>
          </Col>
        </Row>
      )
    }

    return (
      <Row style={{ paddingBottom: '10px' }}>
        <Col sm={0} xs={9}>
          <Text className={classes.text}>{t('page.rabbitPayAccount.apiKey')}</Text>
        </Col>
        <Col sm={23} xs={14}>
          <Text className={classes.text}>{apiKeyStatus.maskedKey}</Text>
        </Col>
        <Col sm={0} xs={1}>
          <MinusCircleOutlined onClick={handleDeleteApiKey} />
        </Col>
        <Col sm={1} xs={0}>
          <MinusCircleOutlined onClick={handleDeleteApiKey} />
        </Col>
      </Row>
    )
  }

  function AddApiKeyForm() {
    return (
      <Form
        name='basic'
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        onFinish={onFinish}
        autoComplete='off'
      >
        <Form.Item
          label={t('page.rabbitPayAccount.apiKey')}
          name='apiKey'
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item className={classes.container}>
          <Button type='primary' htmlType='submit' className={classes.btn}>
            {t('app.common.submit')}
          </Button>
        </Form.Item>
      </Form>
    )
  }

  function AddKeyButton() {
    if (!isAddingKey) {
      return (
        <Button
          type='primary'
          className={classes.btn}
          onClick={() => {
            setIsAddingKey(true)
          }}
        >
          {t('page.rabbitPayAccount.addKey')}
        </Button>
      )
    }
    return <AddApiKeyForm />
  }

  return (
    <PageContainer
      ghost
      header={{
        title: t('page.rabbitPayAccount.title'),
      }}
    >
      <Row className={classes.container}>
        <Col xs={24} xl={14}>
          <ReportCard>
            <Row>
              <Col sm={23} xs={0}>
                <Text className={classes.text}>{t('page.rabbitPayAccount.apiKey')}</Text>
              </Col>
              <Col sm={24} xs={0}>
                <Divider style={{ borderColor: colors.black, borderWidth: 2 }} />
              </Col>
            </Row>
            <CurrentApiKey />
            {!apiKeyStatus.isSet && (
              <>
                <Divider style={{ borderColor: colors.textTertiary, borderWidth: 1 }} />
                <AddKeyButton />
              </>
            )}
          </ReportCard>
        </Col>
      </Row>
    </PageContainer>
  )
}
